import { Column, ColumnDef } from "@tanstack/react-table";

import type { FeatureCollection, Geometry } from "geojson";

import { ArrowSortIcon } from "@/assets/icons";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import BadgeDataType from "@/components/ui/badge-data-type";
import { normalizeFilterValue } from "@/lib/utils";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetColumnConfig,
  DatasetSchemaLabel,
  SchemaToDatasetMap
} from "@/types/dataset";
import {
  createDateSortingFn,
  isDatetime,
  translateToUserTimezone
} from "@/utils/date";
import { createNumericSortingFn } from "@/utils/number";

import { Button } from "equator-design-system";

export interface GenerateColumnsProps extends DatasetColumnConfig {
  columnsConfig: DatasetAttributeSchema[];
}

export type Dataset = {
  id: string;
  geometry: FeatureCollection<Geometry>["features"][number]["geometry"];
  name: string;
};

export type ExtendedColumnDef<TData, TValue> = ColumnDef<TData, TValue> & {
  type?: DataSchemaType;
  data_label?: DatasetSchemaLabel;
  is_reference_geo?: boolean;
};

export function HeaderCell({
  column,
  title,
  type
}: Readonly<{
  column: Column<DatasetDetailRow>;
  title: string;
  type: DataSchemaType;
}>) {
  return (
    <div className="flex flex-col items-start h-full gap-1">
      <div className="flex flex-row items-center w-full gap-2">
        <Button
          variant="ghost"
          className="flex flex-row items-center h-auto p-0 overflow-x-hidden"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <p
            className="text-xs font-semibold text-txtBody line-clamp-1 text-ellipsis"
            title={title}
          >
            {title}
          </p>

          <ArrowSortIcon className="min-w-4" width={16} height={16} />
        </Button>
      </div>

      <div className="flex flex-row items-center gap-2 mt-auto">
        <BadgeDataType type={type} />
      </div>
    </div>
  );
}

export const generateColumns = ({
  columnsConfig,
  timezone,
  isShowActionMapPreviewEnabled,
  handlePreviewClick,
  getPreviewDataFromRow
}: GenerateColumnsProps): ExtendedColumnDef<
  DatasetDetailRow,
  string | number | Date | null
>[] => {
  const visibleColumns = columnsConfig.filter(
    (col) => col.is_visible !== false
  );

  const column = visibleColumns.map((col) => {
    const columnDef: ExtendedColumnDef<
      DatasetDetailRow,
      string | number | Date | null
    > = {
      accessorFn: (row) => {
        const attributeName = normalizeFilterValue(
          col.attribute_column || col.attribute_name
        );

        if (row[attributeName] === null || row[attributeName] === undefined) {
          return null;
        }

        if (isDatetime(col.data_type)) {
          return translateToUserTimezone(
            timezone,
            SchemaToDatasetMap[col.data_type],
            String(row[attributeName])
          );
        }

        if (
          [DataSchemaType.FLOAT, DataSchemaType.INT].includes(col.data_type)
        ) {
          const numValue = Number.parseFloat(String(row[attributeName]));
          return Number.isNaN(numValue) ? null : numValue;
        }

        return String(row[attributeName]);
      },
      accessorKey: normalizeFilterValue(
        col.attribute_column || col.attribute_name
      ),
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title={col.attribute_name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")}
          type={col.data_type}
        />
      ),
      cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <p className="text-txtBody text-xs truncate max-w-[200px]">
            {value === null || value === undefined ? "" : String(value)}
          </p>
        );
      },
      filterFn: "includesString",
      type: col.data_type,
      data_label: col.data_label,
      is_reference_geo: col.is_reference_geo,
      enableSorting: true,
      sortingFn: (() => {
        if (isDatetime(col.data_type)) {
          return createDateSortingFn();
        }
        if (
          [DataSchemaType.FLOAT, DataSchemaType.INT].includes(col.data_type)
        ) {
          return createNumericSortingFn();
        }
        return "basic";
      })()
    };

    return columnDef;
  });

  if (isShowActionMapPreviewEnabled) {
    column.push({
      id: "action",
      accessorKey: "action",
      header: () => (
        <p className="text-xs font-semibold text-center text-txtBody">Action</p>
      ),
      cell: (data) => {
        const { value, type } = getPreviewDataFromRow?.(data.row) ?? {
          value: null,
          type: "wkt" as const
        };

        return (
          <Button
            className="w-full h-5 text-blue-500"
            onClick={() => handlePreviewClick?.(value, type)}
            variant="ghost"
            type="button"
          >
            Preview on Map
          </Button>
        );
      },
      size: 160
    });
  }
  return column;
};
